import produce from 'immer';
import reactUpdate, {Spec} from 'immutability-helper';

import {assert} from 'Asserts';
import Editor from 'Editor';
import {isStructuredTableNotePart} from 'notebook/notes/groups/util/assertIsStructuredTableNotePart';
import {getTablePlugin} from 'notebook/notes/plugins/getTablePlugin';
import {FullTableMap} from 'notebook/tables/excess/Evaluator';
import {getTableFromNotePart, getTableNotePartIndex} from 'notebook/tables/store/selectors/TableSelectors';
import {Cell, Table, TableIndex} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {sanitizeTypedValueForSave} from 'notebook/tables/typed-values/DataTypes';
import {isPlainTable} from 'notebook/TypePredicates';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';
import {omit} from 'util/omitKey';

export function getNotePartSpecFromTable(table: Table, notePart: NotePart): Spec<NotePart> {
  const plugin = getTablePlugin(notePart);
  // Neither the table or structured table plugins use this editor instance, so it does not need to be real.
  return plugin.setContent.call({} as Editor, {
    table,
  }) as Spec<NotePart>;
}

export function updateNoteWithTableMap(tableMap: FullTableMap, note: Note): Note {
  const itemUpdates: {[i: number]: Spec<NotePart>} = {};
  for (let i = 0; i < note.noteParts.length; i++) {
    const notePart = note.noteParts[i];
    if (isPlainTable(notePart) || isStructuredTableNotePart(notePart)) {
      const oldTable = getTableFromNotePart(notePart).table;

      const newTable = tableMap[oldTable.name];
      if (oldTable === newTable) {
        continue;
      }
      if (TableUtil.isInvalidOpWithWarn(oldTable, newTable, {})) {
        return note;
      }

      itemUpdates[i] = getNotePartSpecFromTable(newTable, notePart);
    }
  }
  return reactUpdate(note, {noteParts: itemUpdates});
}

export function updateTableInNote(note: Note, table: Table): Note {
  const notePartIndex = getTableNotePartIndex(note, table.name);
  assert(notePartIndex != null, `Note part index not found for ${table.name}`);
  const notePart = note.noteParts[notePartIndex];
  const updatedNotePartSpec = getNotePartSpecFromTable(table, notePart);
  return reactUpdate(note, {
    noteParts: {[notePartIndex]: updatedNotePartSpec},
  });
}

export function updateCellInTable(
  table: Table,
  [rowIndex, cellIndex]: TableIndex,
  cellData: Partial<Cell>
): Table {
  let oldCell = table.table[rowIndex][cellIndex];
  // Any usage of cell.error should be computed and automatically reapplied by the store if still applicable
  oldCell = omit(oldCell, 'error', 'typedValue');
  const newCell: Cell = {...oldCell, ...cellData};
  if (cellData.typedValue != null) {
    newCell.typedValue = sanitizeTypedValueForSave(cellData.typedValue);
  }
  const updatedTable = produce(table, (draftTable) => {
    draftTable.table[rowIndex][cellIndex] = newCell;
  });
  return updatedTable;
}
